import { formatStringList } from '../decom-policy-utils.tsx';
import { decomActions } from '../../../../../shared/decom-actions.tsx';
import { decomNarratives } from './decom-review-narratives.tsx';
import { DecomActionModel, DecomModel } from '../../../../../../types.ts';
import SharedDetailAccordionNew from '../../../../../shared/shared-detail-accordion-new.tsx';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../../../shared/shared-review-layout-new.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import ExpandCollapseAllButton from '../../../../../shared/buttons/expand-collapse-all-button.tsx';
import { useState } from 'react';
import { TargetType } from '../../../../../shared/shared-review-layout-item.tsx';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../../../shared/shared-review-layout-main-card.tsx';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';

interface ReviewStepProps {
  selectedPolicy: DecomModel;
  pageMode?: string;
  isDrawer?: boolean;
}

export default function ReviewStep({
  selectedPolicy,
  pageMode,
  isDrawer = false,
}: ReviewStepProps) {
  const theme = useTheme();

  const { currentTier, apiUrl } = useUserSettings();
  const actionsData = selectedPolicy.actions || [];
  const grouped = Object.values(actionsData ?? {})
    .filter(Boolean)
    .flatMap(
      (dayData) =>
        dayData?.actions?.map((action) => ({
          ...action,
          day: dayData.day,
        })) || []
    )
    .reduce(
      (acc, action) => {
        const { day } = action;
        if (!acc[day]) acc[day] = [];
        acc[day].push(action);
        return acc;
      },
      {} as Record<number, DecomActionModel[]>
    );

  const sortedDays = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b);

  const renderNotificationEmailList = (userToNotify: string[]) => {
    return (
      <>
        {' '}
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {formatStringList(userToNotify)}
        </Box>{' '}
        will be notified on completion.
      </>
    );
  };

  const renderAccordionDetail = (day: number) => {
    return grouped[day].map((action, actionIndex) => {
      const decomAction = decomActions.find(
        (a) => action.actionType === a.value
      );

      if (!decomAction) return null;

      const summary = decomNarratives[decomAction.value];
      const summaryParams = {
        ...action,
        gPanelTier: currentTier,
      };

      const summaryDetail =
        typeof summary === 'function' ? summary(summaryParams) : summary;

      return (
        <Box
          key={decomAction.value}
          flexDirection="column"
          sx={{
            px: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.semantic.text.support }}
          >
            {decomAction.groupType}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.semantic.text.title2,
            }}
          >
            {decomAction.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.semantic.text.body }}
          >
            {summaryDetail}
            {action.userToNotify?.length > 0
              ? renderNotificationEmailList(action.userToNotify)
              : null}
          </Typography>
          {grouped[day].length - 1 !== actionIndex && (
            <Divider
              sx={{
                color: theme.palette.semantic.outline.light,
                mt: 2,
              }}
            />
          )}
        </Box>
      );
    });
  };

  const allSortedDays = sortedDays.map((day) => day.toString());
  const [expandedPanels, setExpandedPanels] = useState<string[]>(allSortedDays);

  const isAllExpanded =
    sortedDays.length > 0 && expandedPanels.length === sortedDays.length;

  const handleExpandCollapseAll = () => {
    if (isAllExpanded) {
      setExpandedPanels([]);
    } else {
      setExpandedPanels(allSortedDays);
    }
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, expanded: boolean) => {
      event.stopPropagation();
      setExpandedPanels((prev) =>
        expanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    };

  const isFormPage =
    (pageMode === 'create' || pageMode === 'clone' || pageMode === 'edit') &&
    !isDrawer;

  const formatBool = (val?: boolean) => (val ? 'Yes' : 'No');

  const mainSection: MainSectionItem = {
    mainTitle: selectedPolicy.name,
    status: selectedPolicy.isActive ? 'ACTIVE' : 'DISABLED',
    fields: [
      {
        label: 'Last Updated',
        value: new Date(selectedPolicy.lastUpdatedTime).toLocaleString(),
        icon: Edit,
      },
      {
        label: 'Created',
        value: new Date(selectedPolicy.createdTime).toLocaleString(),
        icon: Check,
      },
    ],
  };

  const leftSections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        labelWidth: isDrawer ? 6.5 : 6,
        valueWidth: isDrawer ? 5.5 : 6,
      },
      leftItems: [
        { label: 'Policy Name:', value: selectedPolicy.name },
        {
          label: 'Created By:',
          value: selectedPolicy.createdBy
            ? [{ email: selectedPolicy.createdBy, type: 'User' as const }]
            : [],
          variant: 'chip',
        },
        {
          label: 'Last Updated By:',
          value: selectedPolicy.lastUpdatedBy
            ? [{ email: selectedPolicy.lastUpdatedBy, type: 'User' as const }]
            : [],
          variant: 'chip',
          isVisible: !isFormPage,
        },
        {
          label: 'Trigger Type:',
          value:
            selectedPolicy.triggerType === 'suspension'
              ? 'User Suspension'
              : 'Org Unit Change',
        },
        {
          label: 'Act Immediately:',
          value: formatBool(selectedPolicy.actImmediately),
        },
        {
          label: 'Apply Decommissioning Policy to:',
          value:
            selectedPolicy.org && selectedPolicy.org.length > 0
              ? selectedPolicy.org.map((target) => ({
                  email: target,
                  type: 'OrgUnit' as const,
                }))
              : [],
          variant: 'chip',
          isVisible: !!(selectedPolicy.org && selectedPolicy.org.length > 0),
        },
        {
          label: 'Send Summary Email:',
          value: selectedPolicy.sendSummary,
        },
        {
          label: 'Summary Email:',
          value: selectedPolicy.notificationUser
            ? [
                {
                  email: selectedPolicy.notificationUser,
                  type: 'Unknown' as const,
                },
              ]
            : [],
          isVisible: !!selectedPolicy.notificationUser,
          variant: 'chip',
        },
        {
          label: 'Leave Unsuspended:',
          value: formatBool(selectedPolicy.keepUnsuspended),
        },
        {
          label: 'Pause Decom on User Org Change:',
          value: formatBool(selectedPolicy.validateUserIsStillInDecomOrg),
        },
        {
          label: 'Pause Decom on Unsuspension of User:',
          value: formatBool(selectedPolicy.validateUserIsStillSuspended),
        },
        {
          label: 'Decommissioning ID:',
          value: selectedPolicy.id,
          isVisible: !!(
            currentTier === 'enterprise' &&
            !isFormPage &&
            selectedPolicy.id
          ),
        },
        {
          label: 'API Endpoint:',
          value: `${apiUrl}/v1/policy/decommissioning`,
          isVisible: !!(
            currentTier === 'enterprise' &&
            !isFormPage &&
            apiUrl &&
            apiUrl.length > 0
          ),
        },
      ],
    },
    {
      title: 'Action Configuration',
      leftWidth: {
        labelWidth: isDrawer ? 6.5 : 6,
        valueWidth: isDrawer ? 5.5 : 6,
      },
      leftItems: [
        {
          label: 'Default Emails to Notify:',
          value:
            selectedPolicy.defaultUserToNotify &&
            selectedPolicy.defaultUserToNotify.length > 0
              ? selectedPolicy.defaultUserToNotify.map((email) => ({
                  email: email,
                  type: 'Unknown' as const,
                }))
              : [],
          variant: 'chip',
          isVisible: !!(
            selectedPolicy.defaultUserToNotify &&
            selectedPolicy.defaultUserToNotify.length > 0
          ),
        },
        {
          label: 'Default Destination User:',
          value:
            selectedPolicy.defaultUseManagerEmail === true
              ? [{ email: 'Manager', type: 'Unknown' as const }]
              : typeof selectedPolicy.defaultDestUser === 'undefined' ||
                selectedPolicy.defaultDestUser === null
              ? 'No Default Destination User'
              : [
                  {
                    email: selectedPolicy.defaultDestUser.userEmail,
                    type: selectedPolicy.defaultDestUser.type as TargetType,
                  },
                ],
          variant: 'chip',
        },
      ],
    },
  ];

  const rightSections: ReviewSection[] = [
    {
      title: 'Actions',
      headerButton: (
        <ExpandCollapseAllButton
          size="small"
          expanded={isAllExpanded}
          onClick={handleExpandCollapseAll}
        />
      ),
      leftWidth: {
        labelWidth: 0,
        valueWidth: 12,
      },
      leftItems: [
        {
          value: (
            <Box
              sx={{
                backgroundColor: isDrawer
                  ? theme.palette.semantic.surface.bg1
                  : theme.palette.semantic.surface.bg2,
                p: isDrawer ? 0 : 2,
                width: '100%',
                borderRadius: 1,
              }}
            >
              <Stack spacing={1} flexDirection="column">
                {sortedDays.map((day) => (
                  <SharedDetailAccordionNew
                    key={day}
                    index={day.toString()}
                    title={day === 0 ? 'First Run' : `${day} Days Out`}
                    expanded={expandedPanels.includes(day.toString())}
                    onChange={handleAccordionChange(day.toString())}
                    detail={renderAccordionDetail(day)}
                  />
                ))}
              </Stack>
            </Box>
          ),
        },
      ],
    },
  ];

  return (
    <>
      {isDrawer && <SharedReviewLayoutMainCard section={mainSection} />}

      <SharedReviewLayoutNew
        leftSections={leftSections}
        rightSections={rightSections}
        isDrawer={isDrawer}
      />
    </>
  );
}
